import { useCallback } from 'react'
// @mui
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Stack, { StackProps } from '@mui/material/Stack'
import { SxProps, Typography } from '@mui/material'
// types

// components
import Iconify from 'src/components/iconify'
import { IReportTableFilterValue, IReportTableFilters } from 'src/types/reports'
import { shortDateLabel } from 'src/utils/utils'

// ----------------------------------------------------------------------

type Props = {
  filters: IReportTableFilters
  onFilters: (name: string, value: IReportTableFilterValue) => void
  onResetFilters: VoidFunction
  results: number
  sx?: SxProps
}

export default function ReportTableFiltersResult({
  filters,
  onFilters,
  onResetFilters,
  results,
  sx,
  ...other
}: Props) {
  const handleRemoveEvent = useCallback(() => {
    onFilters('event', null)
  }, [onFilters])

  const handleRemoveDateStart = useCallback(() => {
    onFilters('dateStart', null)
  }, [onFilters])

  const handleRemoveDateEnd = useCallback(() => {
    onFilters('dateEnd', null)
  }, [onFilters])

  const handleRemoveSaler = useCallback(
    (salerToRemove: any) => {
      onFilters(
        'salers',
        filters.salers.filter((saler) => saler.value !== salerToRemove.value),
      )
    },
    [onFilters, filters.salers],
  )

  const handleRemoveTerminalSerial = useCallback(() => {
    onFilters('terminalSerial', '')
  }, [onFilters])

  const handleRemoveCategory = useCallback(
    (categoryToRemove: any) => {
      onFilters(
        'categories',
        (filters.categories || []).filter(
          (category) => category.value !== categoryToRemove.value,
        ),
      )
    },
    [onFilters, filters.categories],
  )

  const formatDate = (date: Date | null) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <Box
      sx={{
        p: 2.5,
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(168, 85, 247, 0.04) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(99, 102, 241, 0.1)',
        ...sx,
      }}
      {...other}
    >
      <Stack spacing={1.5}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Iconify icon='eva:funnel-fill' width={20} height={20} sx={{ color: 'primary.main' }} />
          <Typography variant='subtitle2' sx={{ fontWeight: 600, color: 'text.primary' }}>
            Filtros Aplicados
          </Typography>
          <Chip
            label={`${results} resultados`}
            size='small'
            sx={{
              backgroundColor: 'success.main',
              color: 'white',
              fontWeight: 600,
              ml: 'auto',
            }}
          />
        </Stack>

        <Stack direction='row' flexWrap='wrap' spacing={1}>
          {filters.event && (
            <Chip
              size='small'
              label={`Evento: ${filters.event.label}`}
              onDelete={handleRemoveEvent}
              deleteIcon={<Iconify icon='eva:close-fill' width={16} height={16} />}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          )}

          {filters.dateStart && (
            <Chip
              size='small'
              label={`De: ${formatDate(filters.dateStart)}`}
              onDelete={handleRemoveDateStart}
              deleteIcon={<Iconify icon='eva:close-fill' width={16} height={16} />}
              sx={{
                backgroundColor: 'info.main',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          )}

          {filters.dateEnd && (
            <Chip
              size='small'
              label={`Até: ${formatDate(filters.dateEnd)}`}
              onDelete={handleRemoveDateEnd}
              deleteIcon={<Iconify icon='eva:close-fill' width={16} height={16} />}
              sx={{
                backgroundColor: 'warning.main',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          )}

          {filters.salers?.map((saler: any) => (
            <Chip
              key={saler.value}
              size='small'
              label={`Vendedor: ${saler.label}`}
              onDelete={() => handleRemoveSaler(saler)}
              deleteIcon={<Iconify icon='eva:close-fill' width={16} height={16} />}
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ))}

          {filters.categories?.map((category: any) => (
            <Chip
              key={category.value}
              size='small'
              label={`Categoria: ${category.label}`}
              onDelete={() => handleRemoveCategory(category)}
              deleteIcon={<Iconify icon='eva:close-fill' width={16} height={16} />}
              sx={{
                backgroundColor: 'info.main',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ))}

          {filters.terminalSerial && (
            <Chip
              size='small'
              label={`Terminal: ${filters.terminalSerial}`}
              onDelete={handleRemoveTerminalSerial}
              deleteIcon={<Iconify icon='eva:close-fill' width={16} height={16} />}
              sx={{
                backgroundColor: 'success.main',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          )}

          <Button
            color='error'
            onClick={onResetFilters}
            startIcon={<Iconify icon='eva:trash-2-outline' width={16} height={16} />}
            size='small'
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              px: 2,
              minHeight: 28,
              '&:hover': {
                backgroundColor: 'error.main',
                color: 'white',
              },
            }}
          >
            Limpar filtros
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

// ----------------------------------------------------------------------

type BlockProps = StackProps & {
  label: string
}

function Block({ label, children, sx, ...other }: BlockProps) {
  return (
    <Stack
      component={Paper}
      variant='outlined'
      spacing={1}
      direction='row'
      sx={{
        p: 1,
        borderRadius: 1,
        overflow: 'hidden',
        borderStyle: 'dashed',
        ...sx,
      }}
      {...other}
    >
      <Box component='span' sx={{ typography: 'subtitle2' }}>
        {label}
      </Box>

      <Stack spacing={1} direction='row' flexWrap='wrap'>
        {children}
      </Stack>
    </Stack>
  )
}
